import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ConfirmEmailDTO, MemberClient, MemberDTO, MemberResetPasswordDTO, MemberProfileDTO, UpdateMemberStatusDTO, BulkMemberIdsDTO, UpdateMemberRegistrationStepDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';

export const useMemberApi = () => {
    const apiClient = new MemberClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: MemberDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createMember(data);
        } catch (err: any) {
            setError(err.message || 'Error creating Member');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: MemberDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateMember(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating Member');
        } finally {
            setIsLoading(false);
        }
    };

    // Narrow registration-step update for the member setup wizard. Throws (409) once the member is no
    // longer a new registration, so callers must refetch the member rather than assume the step moved.
    const updateRegistrationStep = async (id: string, data: UpdateMemberRegistrationStepDTO) => {
        setIsLoading(true);
        try {
            return await apiClient.updateMemberRegistrationProgress(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating registration step');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getMemberById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching Member');
        } finally {
            setIsLoading(false);
        }
    };

    const getSimplifiedById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getMemberSimplifiedById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching simplified Member details');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchMembers(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching Members');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteMember(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting Member');
        } finally {
            setIsLoading(false);
        }
    };

    const archive = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.archiveMember(id);
        } catch (err: any) {
            setError(err.message || 'Error archiving Member');
        } finally {
            setIsLoading(false);
        }
    };

    const restoreFromArchive = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.restoreFromArchiveMember(id);
        } catch (err: any) {
            setError(err.message || 'Error restoring Member from archive');
        } finally {
            setIsLoading(false);
        }
    };

    const restoreFromDeleted = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.restoreFromDeletedMember(id);
        } catch (err: any) {
            setError(err.message || 'Error restoring Member from deleted state');
            throw err; // Re-throw so component can handle it
        } finally {
            setIsLoading(false);
        }
    };

    const checkIdExists = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.checkIdExists(id);
        } catch (err: any) {
            setError(err.message || 'Error checking if Id exists');
        } finally {
            setIsLoading(false);
        }
    };

    const resetPassword = async (data: MemberResetPasswordDTO): Promise<boolean> => {
        setIsLoading(true);
        try {
            const response = await apiClient.resetMemberPassword(data.memberId!, data);
            return !!(response?.success && response?.data === true);
        } catch (err: any) {
            setError(err.message || 'Error resetting member password');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getCertificate = async (id: string): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getMemberCertificate(id);
            return response?.data || '';
        } catch (err: any) {
            setError(err.message || 'Error getting member certificate');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getHtmlCertificate = async (id: string): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getMemberHtmlCertificate(id);
            return response?.data || '';
        } catch (err: any) {
            setError(err.message || 'Error getting member HTML certificate');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getMemberCard = async (id: string): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getMemberCard(id);
            return response?.data || '';
        } catch (err: any) {
            setError(err.message || 'Error getting member card');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getHtmlMemberCard = async (id: string): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getMemberHtmlCard(id);
            return response?.data || '';
        } catch (err: any) {
            setError(err.message || 'Error getting member HTML card');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getMandatePdf = async (id: string): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getMemberDebitOrderMandate(id);
            return response?.data || '';
        } catch (err: any) {
            setError(err.message || 'Error getting debit order mandate');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getAnniversaryCertificate = async (id: string, years: number): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getAnniversaryCertificate(id, years);
            return response?.data || '';
        } catch (err: any) {
            setError(err.message || 'Error getting anniversary certificate');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getHtmlAnniversaryCertificate = async (id: string, years: number): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getAnniversaryHtmlCertificate(id, years);
            return response?.data || '';
        } catch (err: any) {
            setError(err.message || 'Error getting anniversary HTML certificate');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getReachedMilestones = async (id: string): Promise<number[]> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getAnniversaryMilestones(id);
            return response?.data || [];
        } catch (err: any) {
            setError(err.message || 'Error getting anniversary milestones');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const confirmEmail = async (data: ConfirmEmailDTO) => {
        setIsLoading(true);
        try {
            const response = await apiClient.confirmMemberEmail(data);
            return response;
        } catch (err: any) {
            console.error('Error confirming email:', err);
            setError(err.response?.data?.message || 'Failed to confirm email');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const updateProofOfPayment = async (id: string, fileRecordId: string) => {
        setIsLoading(true);
        try {
            await apiClient.updateMemberProofOfPayment(id, fileRecordId);
        } catch (err: any) {
            setError(err.message || 'Error updating proof of payment');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const sendProofOfPaymentLink = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.sendMemberProofOfPaymentLink(id);
        } catch (err: any) {
            setError(err.message || 'Error updating proof of payment');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getProfile = async () => {
        setIsLoading(true);
        try {
            return await apiClient.getMemberProfile();
        } catch (err: any) {
            setError(err.message || 'Error fetching member profile');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const editProfile = async (data: MemberProfileDTO) => {
        setIsLoading(true);
        try {
            return await apiClient.updateMemberProfile(data);
        } catch (err: any) {
            setError(err.message || 'Error updating member profile');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const updateStatus = async (data: UpdateMemberStatusDTO) => {
        setIsLoading(true);
        try {
            return await apiClient.updateMemberQuickStatus(data);
        } catch (err: any) {
            setError(err.message || 'Error updating member status');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const bulkArchive = async (recordIds: string[]) => {
        setIsLoading(true);
        try {
            const data: BulkMemberIdsDTO = { recordIds };
            return await apiClient.bulkArchiveMembers(data);
        } catch (err: any) {
            setError(err.message || 'Error archiving members');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const bulkDelete = async (recordIds: string[]) => {
        setIsLoading(true);
        try {
            const data: BulkMemberIdsDTO = { recordIds };
            return await apiClient.bulkDeleteMembers(data);
        } catch (err: any) {
            setError(err.message || 'Error deleting members');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const extract = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.exportMembers(filters);
        } catch (err: any) {
            setError(err.message || 'Error exporting Members');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const exportLifeMembersReport = async (fromDate?: string, toDate?: string) => {
        setIsLoading(true);
        try {
            return await apiClient.exportLifeMembersReport(fromDate, toDate);
        } catch (err: any) {
            setError(err.message || 'Error exporting Life Members report');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const lookupMemberByCertificate = async (componentId: string, certificateCode: string) => {
        setIsLoading(true);
        try {
            return await apiClient.lookupMemberByCertificate({ componentId, certificateCode });
        } catch (err: any) {
            setError(err.message || 'Error looking up member by certificate');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        create,
        edit,
        updateRegistrationStep,
        getById,
        getSimplifiedById,
        search,
        remove,
        archive,
        restoreFromArchive,
        restoreFromDeleted,
        checkIdExists,
        resetPassword,
        getCertificate,
        getHtmlCertificate,
        getMemberCard,
        getHtmlMemberCard,
        getMandatePdf,
        getAnniversaryCertificate,
        getHtmlAnniversaryCertificate,
        getReachedMilestones,
        confirmEmail,
        updateProofOfPayment,
        sendProofOfPaymentLink,
        getProfile,
        editProfile,
        updateStatus,
        bulkArchive,
        bulkDelete,
        extract,
        exportLifeMembersReport,
        lookupMemberByCertificate,
        isLoading,
        error
    };
};

const getMemberClient = () => new MemberClient(API_BASE_URL, axiosInstance);

export const useMemberById = (id: string | undefined) =>
  useQuery({
    queryKey: ['member', id],
    queryFn: async () => {
      const response = await getMemberClient().getMemberById(id!);
      return response?.data || null;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    refetchOnReconnect: true,
  });